/**
 * Die Kennzahlen einer Rohwertreihe als Kacheln (HM-053, geteilt seit HM-066).
 *
 * **Ein Stand und ein Momentanwert werden verschieden befragt.** Beim Stand
 * lautet die Frage „wie viel ist dazugekommen", beim Momentanwert „wie hoch ist
 * er geworden" — Minimum und Maximum eines aufwärtszählenden Zählers sind
 * dagegen nur Anfang und Ende und deshalb keine Kachel wert.
 *
 * **Sie steht hier und nicht in einer Route**, weil zwei Seiten dieselbe Reihe
 * befragen: der Verlauf eines Datenpunkts und der Zähler, dessen Zählwerk
 * daraus gespeist wird. Zwei Fassungen liefen beim nächsten Feld der Statistik
 * auseinander — und die eine, die eine Zahl mehr zeigt, sähe aus, als hätte sie
 * mehr Daten.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { formatDatapointValue } from "../lib/datapoint-format.js";
import { formatTimeAgo } from "../lib/format.js";

type Datapoint = ApiComponents["schemas"]["Datapoint"];
type History = ApiComponents["schemas"]["DatapointHistoryResponse"];

const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;

export function HistoryTiles({
  history,
  stats,
  point,
}: {
  readonly history: History;
  readonly stats: NonNullable<History["statistics"]>;
  readonly point: Datapoint;
}) {
  const counter = history.aggregate === "last";
  /* Jede Kachel steht für sich und bekommt deshalb die Größenordnung ihres
     eigenen Werts — „70,9 W" und nicht „0,1 kW", nur weil die Reihe eine Spitze
     von 2,8 kW enthält. Gemeinsam ist die Stufe dort, wo Zahlen untereinander
     stehen: an der Achse und in der Tabelle. */
  const show = (value: number): string => formatDatapointValue(value, point);

  const tiles = counter
    ? [
        { label: "Stand am Ende", value: show(stats.last.v) },
        {
          label: "Zuwachs im Zeitraum",
          /* `null` heißt: dazwischen liegt ein Rücksprung. Der Kasten darüber
             sagt warum; hier steht kein Strich, sondern das Wort dafür. */
          value: stats.increase === null ? "nicht bestimmbar" : show(stats.increase),
        },
        /* Bis HM-182 stand hier „Rohwerte 1972" — eine Zahl, mit der niemand etwas
           anfangen kann. Wann der Stand zuletzt kam, ist die Frage daneben. */
        { label: "Letzte Meldung", value: formatTimeAgo(stats.last.t) },
      ]
    : [
        { label: "Zuletzt", value: show(stats.last.v) },
        { label: "Kleinster Wert", value: show(stats.min) },
        { label: "Größter Wert", value: show(stats.max) },
        { label: "Mittel", value: show(stats.mean) },
      ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
        gap: "var(--sp-3)",
      }}
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          style={{
            padding: "12px 14px",
            borderRadius: "var(--radius-lg)",
            background: "var(--surface-inset)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: "var(--tracking-caps)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
            }}
          >
            {tile.label}
          </div>
          <div
            style={{
              ...MONO,
              marginTop: 4,
              fontSize: 17,
              fontWeight: 700,
              color: "var(--text-strong)",
            }}
          >
            {tile.value}
          </div>
        </div>
      ))}
    </div>
  );
}
